import { Injectable, inject } from "@angular/core";
import { Actions } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AlertController } from "@ionic/angular";
import {
  FormGroupState,
  MarkAsDirtyAction,
  MarkAsPristineAction,
  SetValueAction,
  markAsDirty,
  markAsPristine,
  setValue,
} from "ngrx-forms";
import {
  Observable,
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  skip,
  switchMap,
  take,
  tap,
} from "rxjs";
import { AuthService } from "./auth.service";
import {
  FormDraftDocument,
  FormDraftKey,
  FormDraftService,
} from "./form-draft.service";

export interface FormDraftSessionConfig<T> {
  formType: string;
  mode: "create" | "edit";
  contextId: string;
  sessionId?: string;
  form$: Observable<FormGroupState<T>>;
  saveSuccessActionTypes: string[];
  saveFailureActionTypes?: string[];
  formVersion?: number;
}

interface QueuedDraftWrite {
  key: FormDraftKey;
  value: unknown;
  baselineFingerprint: string;
  formVersion: number;
}

@Injectable({ providedIn: "root" })
export class FormDraftSessionService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly alertController = inject(AlertController);
  private readonly authService = inject(AuthService);
  private readonly drafts = inject(FormDraftService);

  private sessionSubscription = new Subscription();
  private successSubscription: Subscription | null = null;
  private activeKey: FormDraftKey | null = null;
  private latestForm: FormGroupState<unknown> | null = null;
  private baselineFingerprint = "";
  private formVersion = 1;
  private saving = false;
  private sessionGeneration = 0;
  private pendingWrite: Promise<void> = Promise.resolve();
  private queuedWrite: QueuedDraftWrite | null = null;
  private writeDrainActive = false;
  private draftConflictAlert: HTMLIonAlertElement | null = null;
  private draftPersistenceBlocked = false;
  private draftResolutionPromise: Promise<void> = Promise.resolve();
  private resolveDraftResolution: (() => void) | null = null;

  public start<T>(config: FormDraftSessionConfig<T>): void {
    this.close(true);
    const sessionGeneration = ++this.sessionGeneration;
    const user = this.authService.getLoggedUser();
    this.activeKey = {
      ownerUserId: user.id,
      companyId: this.authService.getCompany(),
      formType: config.formType,
      mode: config.mode,
      contextId: config.contextId,
      sessionId: config.sessionId,
    };
    this.formVersion = config.formVersion ?? 1;
    this.saving = false;
    this.draftPersistenceBlocked = true;
    this.draftResolutionPromise = new Promise<void>((resolve) => {
      this.resolveDraftResolution = resolve;
    });

    this.sessionSubscription.add(
      config.form$.subscribe(
        (form) => (this.latestForm = form as FormGroupState<unknown>)
      )
    );

    const initializedForm$ = config.form$.pipe(skip(1), take(1));
    this.sessionSubscription.add(
      initializedForm$
        .pipe(
          switchMap((baseForm) => {
            this.latestForm = baseForm as FormGroupState<unknown>;
            this.baselineFingerprint = this.fingerprint(baseForm.value);
            return from(this.pendingWrite.catch(() => undefined)).pipe(
              switchMap(() => this.drafts.load<T>(this.activeKey)),
              catchError((error) => {
                this.reportError("Unable to load form draft", error);
                return of(null);
              }),
              map((draft) => ({ baseForm, draft }))
            );
          })
        )
        .subscribe(({ baseForm, draft }) => {
          if (sessionGeneration !== this.sessionGeneration) return;
          const formWasNotEdited =
            this.latestForm?.isPristine &&
            this.fingerprint(this.latestForm.value) ===
              this.baselineFingerprint;
          const draftSchemaIsCompatible =
            !!draft &&
            draft.schemaVersion === 1 &&
            draft.formVersion === this.formVersion;
          const draftBaselineIsCompatible =
            config.mode === "create" ||
            draft?.baselineFingerprint === this.baselineFingerprint;
          if (
            draft &&
            draftSchemaIsCompatible &&
            draftBaselineIsCompatible &&
            formWasNotEdited
          ) {
            this.latestForm = markAsDirty(
              setValue(baseForm, draft.payload)
            ) as FormGroupState<unknown>;
            this.store.dispatch(new SetValueAction(baseForm.id, draft.payload));
            this.store.dispatch(new MarkAsDirtyAction(baseForm.id));
            this.enableAutosave(config.form$);
            this.unblockDraftPersistence();
            return;
          }

          if (draft) {
            void this.resolveDraftConflict(
              config.form$,
              baseForm,
              draft,
              sessionGeneration,
              draftSchemaIsCompatible
            );
            return;
          }

          this.enableAutosave(config.form$);
          this.unblockDraftPersistence();
        })
    );

    this.successSubscription = new Subscription();
    this.successSubscription.add(this.actions$
      .pipe(
        filter((action) =>
          this.saving && config.saveSuccessActionTypes.includes(action.type)
        ),
        take(1),
        switchMap(() => {
          const key = this.activeKey;
          if (!key) return of(false);
          this.drafts.suppressRestore(key);
          return this.drafts.markCommitted(key).pipe(
            catchError((error) => {
              this.reportError("Unable to mark form draft as committed", error);
              return of(null);
            }),
            switchMap(() => this.drafts.discard(key)),
            catchError((error) => {
              this.reportError("Unable to remove committed form draft", error);
              return of(false);
            })
          );
        })
      )
      .subscribe(() => this.resetSession()));

    if (config.saveFailureActionTypes?.length) {
      this.successSubscription.add(
        this.actions$
          .pipe(
            filter((action) =>
              this.saving &&
              config.saveFailureActionTypes.includes(action.type)
            )
          )
          .subscribe(() => {
            this.saving = false;
            this.enableAutosave(config.form$);
          })
      );
    }
  }

  public async save(dispatchSave: () => void): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    const sessionGeneration = this.sessionGeneration;
    await this.draftResolutionPromise;
    if (sessionGeneration !== this.sessionGeneration) return;
    this.sessionSubscription.unsubscribe();
    this.sessionSubscription = new Subscription();
    try {
      await this.pendingWrite;
      await this.persistLatestForm();
    } catch {
      // Draft persistence must not prevent the explicit business Save action.
    } finally {
      dispatchSave();
    }
  }

  public async keepDraft(): Promise<void> {
    const sessionGeneration = this.sessionGeneration;
    await this.draftResolutionPromise;
    if (sessionGeneration !== this.sessionGeneration) return;
    this.sessionSubscription.unsubscribe();
    await this.pendingWrite;
    await this.persistLatestForm();
  }

  public async flushActiveDraft(): Promise<void> {
    if (
      this.draftPersistenceBlocked ||
      !this.activeKey ||
      !this.latestForm?.isDirty
    ) {
      return;
    }
    this.enqueueWrite(this.latestForm.value);
    await this.pendingWrite;
  }

  public async discardDraft(): Promise<void> {
    this.sessionSubscription.unsubscribe();
    await this.pendingWrite;
    if (this.activeKey) {
      await firstValueFrom(this.drafts.discard(this.activeKey));
    }
    this.successSubscription?.unsubscribe();
    this.successSubscription = null;
    this.resetSession();
  }

  public close(force = false): void {
    this.sessionSubscription.unsubscribe();
    this.sessionSubscription = new Subscription();
    if (force || !this.saving) {
      this.successSubscription?.unsubscribe();
      this.successSubscription = null;
      this.resetSession();
    }
  }

  private enableAutosave<T>(form$: Observable<FormGroupState<T>>): void {
    this.sessionSubscription.add(
      form$
        .pipe(
          tap(
            (form) =>
              (this.latestForm = form as FormGroupState<unknown>)
          ),
          filter(
            (form) =>
              form.isDirty && !this.saving && !this.draftPersistenceBlocked
          ),
          distinctUntilChanged(
            (previous, current) => previous.value === current.value
          ),
          map((form) => {
            this.enqueueWrite(form.value);
            return form;
          })
        )
        .subscribe()
    );
  }

  private enqueueWrite(value: unknown): void {
    const key = this.activeKey;
    const baselineFingerprint = this.baselineFingerprint;
    const formVersion = this.formVersion;
    if (!key) return;

    this.queuedWrite = { key, value, baselineFingerprint, formVersion };
    if (this.writeDrainActive) return;

    this.writeDrainActive = true;
    this.pendingWrite = this.drainWrites().finally(() => {
      this.writeDrainActive = false;
    });
  }

  private async drainWrites(): Promise<void> {
    while (this.queuedWrite) {
      const write = this.queuedWrite;
      this.queuedWrite = null;
      try {
        await this.saveDraftWithRetry(write);
      } catch (error) {
        this.reportError("Unable to autosave form draft", error);
      }
    }
  }

  private async saveDraftWithRetry(
    write: QueuedDraftWrite,
    maxAttempts = 3
  ): Promise<void> {
    let lastError: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await firstValueFrom(
          this.drafts.save(
            write.key,
            write.value,
            write.baselineFingerprint,
            write.formVersion
          )
        );
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  }

  private async persistLatestForm(): Promise<void> {
    if (
      this.draftPersistenceBlocked ||
      !this.activeKey ||
      !this.latestForm?.isDirty
    ) {
      return;
    }
    await firstValueFrom(
      this.drafts.save(
        this.activeKey,
        this.latestForm.value,
        this.baselineFingerprint,
        this.formVersion
      )
    );
  }

  private async resolveDraftConflict<T>(
    form$: Observable<FormGroupState<T>>,
    baseForm: FormGroupState<T>,
    draft: FormDraftDocument<T>,
    sessionGeneration: number,
    draftCanBeRestored: boolean
  ): Promise<void> {
    const key = this.activeKey;
    if (!key || sessionGeneration !== this.sessionGeneration) return;

    const result = await this.presentSessionAlert(
      {
        header: "Draft Conflict",
        message: draftCanBeRestored
          ? "A saved draft conflicts with the current form. Choose which version to continue editing."
          : "This draft was created by an incompatible form version and cannot be restored safely.",
        backdropDismiss: false,
        buttons: [
          ...(draftCanBeRestored
            ? [
                {
                  text: "Use my draft",
                  role: "restoreDraft",
                },
              ]
            : []),
          {
            text: "Use Odoo version",
            role: "useOdoo",
          },
        ],
      },
      sessionGeneration
    );
    if (sessionGeneration !== this.sessionGeneration) return;
    if (!result) return;

    if (draftCanBeRestored && result.role === "restoreDraft") {
      this.latestForm = markAsDirty(
        setValue(baseForm, draft.payload)
      ) as FormGroupState<unknown>;
      this.store.dispatch(new SetValueAction(baseForm.id, draft.payload));
      this.store.dispatch(new MarkAsDirtyAction(baseForm.id));
      this.enableAutosave(form$);
      this.unblockDraftPersistence();
      this.enqueueWrite(draft.payload);
      return;
    }

    if (result.role === "useOdoo") {
      try {
        await firstValueFrom(this.drafts.discard(key));
        if (sessionGeneration === this.sessionGeneration) {
          this.latestForm = markAsPristine(
            setValue(baseForm, baseForm.value)
          ) as FormGroupState<unknown>;
          this.store.dispatch(new SetValueAction(baseForm.id, baseForm.value));
          this.store.dispatch(new MarkAsPristineAction(baseForm.id));
          this.enableAutosave(form$);
          this.unblockDraftPersistence();
        }
        return;
      } catch (error) {
        this.reportError("Unable to discard conflicting form draft", error);
        await this.presentSessionAlert(
          {
            header: "Draft Storage Error",
            message:
              "The previous draft could not be removed safely. Please try again.",
            buttons: ["OK"],
          },
          sessionGeneration
        );
      }
    }

    if (sessionGeneration === this.sessionGeneration) {
      await this.resolveDraftConflict(
        form$,
        baseForm,
        draft,
        sessionGeneration,
        draftCanBeRestored
      );
    }
  }

  private async presentSessionAlert(
    options: Parameters<AlertController["create"]>[0],
    sessionGeneration: number
  ): Promise<{ role?: string } | null> {
    let alert: HTMLIonAlertElement | null = null;
    try {
      alert = await this.alertController.create(options);
      if (sessionGeneration !== this.sessionGeneration) {
        await alert.dismiss();
        return null;
      }
      this.draftConflictAlert = alert;
      await alert.present();
      return await alert.onDidDismiss();
    } catch (error) {
      this.reportError("Unable to present form draft conflict", error);
      return null;
    } finally {
      if (this.draftConflictAlert === alert) {
        this.draftConflictAlert = null;
      }
    }
  }

  private fingerprint(value: unknown): string {
    return JSON.stringify(value);
  }

  private unblockDraftPersistence(): void {
    this.draftPersistenceBlocked = false;
    this.resolveDraftResolution?.();
    this.resolveDraftResolution = null;
  }

  private resetSession(): void {
    this.sessionGeneration++;
    if (this.draftConflictAlert) {
      void this.draftConflictAlert.dismiss();
      this.draftConflictAlert = null;
    }
    this.activeKey = null;
    this.latestForm = null;
    this.baselineFingerprint = "";
    this.saving = false;
    this.unblockDraftPersistence();
  }

  private reportError(message: string, error: unknown): void {
    console.error(message, error);
  }
}
